import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getSeedReport, getAnalysisStatus } from '../utils/api';
import {
  AreaChart, Area, BarChart, Bar, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  ArrowLeft, Download, Loader, AlertCircle, Zap,
  Shield, Swords, TrendingUp, Users, Activity,
  CheckCircle, Bot, RefreshCw
} from 'lucide-react';
import PlayerHeatmap from '../components/PlayerHeatmap';
import './ReportDetailPage.css';

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1a2236', border: '1px solid #1f2d45', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      {label !== undefined && <p style={{ color: '#94a3b8', marginBottom: 4 }}>{typeof label === 'number' ? `Min ${label}` : label}</p>}
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color || '#00d4ff', margin: '2px 0' }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

export default function ReportDetailPage() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [tab, setTab]       = useState('overview');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        // Seed reports have IDs starting with 'seed-'
        // User-uploaded reports are UUIDs — always fetch from /analyze/status
        if (id.startsWith('seed-')) {
          const r = await getSeedReport(id);
          if (!cancelled) setReport(r.data);
        } else {
          const r = await getAnalysisStatus(id);
          if (!cancelled) setReport(r.data);
        }
      } catch (err) {
        if (!cancelled) setError('Report not found. It may have been lost when the server restarted.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return (
    <div className="report-loading">
      <Loader size={32} className="spin" style={{ color: 'var(--accent-primary)' }} />
      <p>Loading report...</p>
    </div>
  );

  if (error) return (
    <div className="report-error">
      <AlertCircle size={28} style={{ color: 'var(--accent-red)' }} />
      <p>{error}</p>
      <Link to="/reports" className="btn btn-secondary">Back to Reports</Link>
    </div>
  );

  if (!report) return (
    <div className="report-error">
      <AlertCircle size={28} />
      <p>Report not found</p>
      <Link to="/reports" className="btn btn-secondary">Back to Reports</Link>
    </div>
  );

  if (report.status === 'processing') return (
    <div className="report-loading">
      <Loader size={32} className="spin" style={{ color: 'var(--accent-primary)' }} />
      <h3>Analysis in Progress</h3>
      <p>Gemini AI is generating your report...</p>
      <button className="btn btn-secondary" style={{ marginTop: 16 }} onClick={() => window.location.reload()}>
        <RefreshCw size={14} /> Refresh
      </button>
    </div>
  );

  if (report.status === 'error' || !report.report) return (
    <div className="report-error">
      <AlertCircle size={28} style={{ color: 'var(--accent-red)' }} />
      <h3 style={{ margin: '8px 0' }}>Analysis Failed</h3>
      <p>{report.error || 'The analysis could not be completed and no report was generated.'}</p>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
        No demo or fallback data is shown here — this reflects a real failure, not a report.
      </p>
      <Link to="/upload" className="btn btn-primary" style={{ marginTop: 16 }}>Try Again</Link>
      <Link to="/reports" className="btn btn-secondary" style={{ marginTop: 8 }}>Back to Reports</Link>
    </div>
  );

  const r    = report.report;
  const home = report.homeTeam || 'Home';
  const away = report.awayTeam || 'Away';

  const TABS = [
    { key: 'overview',  label: 'Overview'   },
    { key: 'tactics',   label: 'Tactics'    },
    { key: 'pressing',  label: 'Pressing'   },
    { key: 'players',   label: 'Players'    },
    { key: 'scouting',  label: 'Opposition' },
    { key: 'coaching',  label: 'Coaching'   },
  ];

  const statRows = r.keyStats ? [
    { name: 'Shots',      home: r.keyStats.shots?.home,           away: r.keyStats.shots?.away },
    { name: 'On Target',  home: r.keyStats.shotsOnTarget?.home,   away: r.keyStats.shotsOnTarget?.away },
    { name: 'Corners',    home: r.keyStats.corners?.home,         away: r.keyStats.corners?.away },
    { name: 'Fouls',      home: r.keyStats.fouls?.home,           away: r.keyStats.fouls?.away },
  ] : [];

  const heatRows = r.heatmapZones ? [
    { zone: 'Left',      home: r.heatmapZones.home?.leftFlank,   away: r.heatmapZones.away?.leftFlank },
    { zone: 'Centre',    home: r.heatmapZones.home?.center,      away: r.heatmapZones.away?.center },
    { zone: 'Right',     home: r.heatmapZones.home?.rightFlank,  away: r.heatmapZones.away?.rightFlank },
    { zone: 'Own Half',  home: r.heatmapZones.home?.ownHalf,     away: r.heatmapZones.away?.ownHalf },
    { zone: 'Final 3rd', home: r.heatmapZones.home?.finalThird,  away: r.heatmapZones.away?.finalThird },
  ] : [];

  const possData = [
    { name: home, value: r.possession?.home ?? 50, fill: '#00d4ff' },
    { name: away, value: r.possession?.away ?? 50, fill: '#7c3aed' },
  ];

  // Only include a spoke when BOTH sides have a real, model-reported value —
  // this previously defaulted missing metrics to plausible-looking baseline
  // numbers (e.g. "5", "50", "75"), which is exactly the kind of invented
  // stat this app is built to never show. An honestly incomplete radar (or
  // no radar at all) is correct; a fabricated one is not.
  const radarCandidates = [
    { m: 'Pressing',   home: r.pressingAnalysis?.home?.intensity,        away: r.pressingAnalysis?.away?.intensity,        scale: 10 },
    { m: 'Defense',    home: r.defensiveStructure?.home?.compactness,    away: r.defensiveStructure?.away?.compactness,    scale: 10 },
    { m: 'Attack',     home: r.attackingPatterns?.home?.chancesCreated,  away: r.attackingPatterns?.away?.chancesCreated,  scale: 5, clamp: 100 },
    { m: 'Possession', home: r.possession?.home,                        away: r.possession?.away,                         scale: 1 },
    { m: 'Pass Acc.',  home: r.keyStats?.passAccuracy?.home,             away: r.keyStats?.passAccuracy?.away,             scale: 1 },
  ];
  const radarData = radarCandidates
    .filter(c => c.home != null && c.away != null)
    .map(c => ({
      m: c.m,
      home: c.clamp ? Math.min(c.clamp, c.home * c.scale) : c.home * c.scale,
      away: c.clamp ? Math.min(c.clamp, c.away * c.scale) : c.away * c.scale,
    }));

  return (
    <div className="report-detail fade-in">

      {/* Topbar */}
      <div className="report-detail__topbar">
        <Link to="/reports" className="btn btn-ghost"><ArrowLeft size={15} /> Back</Link>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {(report.aiGenerated || report.report?.aiGenerated)
            ? <span className="badge badge-purple" style={{ gap: 5 }}><Bot size={11} /> AI Generated</span>
            : <span className="badge badge-amber">Demo Mode</span>
          }
          <button className="btn btn-secondary" onClick={() => window.print()}>
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="report-hero card">
        <div className="report-hero__top">
          <div className="report-hero__badges">
            <span className="badge badge-green"><CheckCircle size={11} /> Complete</span>
            {report.competition && <span className="badge badge-blue">{report.competition}</span>}
            {r.analysisConfidence && <span className="badge badge-purple">Confidence: {r.analysisConfidence}%</span>}
          </div>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{report.matchDate}</span>
        </div>

        <div className="report-hero__match">
          <div className="report-hero__team">
            <div className="hero-badge">{home[0]}</div>
            <div className="report-hero__team-name">{home}</div>
            {r.formations?.home?.name && <div className="report-hero__formation">{r.formations.home.name}</div>}
          </div>

          <div className="report-hero__score">
            <div className="hero-score">
              <span>{r.score?.home ?? '–'}</span>
              <span style={{ color: 'var(--text-muted)', margin: '0 8px' }}>–</span>
              <span>{r.score?.away ?? '–'}</span>
            </div>
            {r.possession && (
              <div className="hero-possession">
                <div className="possession-bar">
                  <div className="possession-bar__home" style={{ width: `${r.possession.home}%` }} />
                </div>
                <div className="possession-labels">
                  <span style={{ color: '#00d4ff' }}>{r.possession.home}%</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>Possession</span>
                  <span style={{ color: '#7c3aed' }}>{r.possession.away}%</span>
                </div>
              </div>
            )}
          </div>

          <div className="report-hero__team report-hero__team--right">
            <div className="hero-badge">{away[0]}</div>
            <div className="report-hero__team-name">{away}</div>
            {r.formations?.away?.name && <div className="report-hero__formation">{r.formations.away.name}</div>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="report-tabs">
        {TABS.map(t => (
          <button key={t.key} className={`report-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── OVERVIEW ─── */}
      {tab === 'overview' && (
        <div className="tab-grid fade-in">
          <div className="card report-section report-section--full">
            <h3 className="report-section__title"><Activity size={14} /> Match Summary</h3>
            <div className="report-summary">
              {(r.matchSummary || 'No summary available.').split('\n\n').map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>

          {statRows.length > 0 && (
            <div className="card report-section">
              <h3 className="report-section__title">Key Statistics</h3>
              <div className="chart-legend"><span style={{color:'#00d4ff'}}>■ {home}</span><span style={{color:'#7c3aed'}}>■ {away}</span></div>
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={statRows} layout="vertical" margin={{ left: 10, right: 10 }}>
                  <XAxis type="number" tick={{ fill: '#475569', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} width={70} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="home" name={home} fill="#00d4ff" radius={[0,3,3,0]} />
                  <Bar dataKey="away" name={away} fill="#7c3aed" radius={[0,3,3,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {r.possession && (
            <div className="card report-section">
              <h3 className="report-section__title">Possession</h3>
              <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                  <Pie data={possData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} dataKey="value" paddingAngle={3}>
                    {possData.map((e,i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Legend formatter={v => <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{v}</span>} />
                  <Tooltip content={<ChartTip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {r.momentumData?.length > 0 && (
            <div className="card report-section report-section--full">
              <h3 className="report-section__title">Match Momentum</h3>
              <div className="chart-legend"><span style={{color:'#00d4ff'}}>■ {home}</span><span style={{color:'#7c3aed'}}>■ {away}</span></div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={r.momentumData} margin={{ left: 0, right: 10 }}>
                  <defs>
                    <linearGradient id="hG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#00d4ff" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00d4ff" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="aG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#7c3aed" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="minute" tick={{ fill:'#475569', fontSize:11 }} axisLine={false} tickLine={false} tickFormatter={v=>`${v}'`}/>
                  <YAxis domain={[0,100]} hide />
                  <Tooltip content={<ChartTip />} />
                  <Area type="monotone" dataKey="home" name={home} stroke="#00d4ff" fill="url(#hG)" strokeWidth={2} dot={false}/>
                  <Area type="monotone" dataKey="away" name={away} stroke="#7c3aed" fill="url(#aG)" strokeWidth={2} dot={false}/>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {radarData.length >= 3 ? (
            <div className="card report-section report-section--full">
              <h3 className="report-section__title">Performance Radar</h3>
              <div className="chart-legend"><span style={{color:'#00d4ff'}}>■ {home}</span><span style={{color:'#7c3aed'}}>■ {away}</span></div>
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius={100}>
                  <PolarGrid stroke="#1f2d45"/>
                  <PolarAngleAxis dataKey="m" tick={{ fill:'#94a3b8', fontSize:12 }}/>
                  <Radar name={home} dataKey="home" stroke="#00d4ff" fill="#00d4ff" fillOpacity={0.2}/>
                  <Radar name={away} dataKey="away" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.15}/>
                  <Tooltip contentStyle={{ background:'#1a2236', border:'1px solid #1f2d45', borderRadius:8, fontSize:12 }}/>
                </RadarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="card report-section report-section--full">
              <h3 className="report-section__title">Performance Radar</h3>
              <div className="empty-state">Not enough comparable tactical metrics were reliably determined from this footage to plot a radar honestly.</div>
            </div>
          )}
        </div>
      )}

      {/* ─── TACTICS ─── */}
      {tab === 'tactics' && (
        <div className="tab-grid fade-in">
          {r.formations && <>
            <div className="card report-section">
              <h3 className="report-section__title"><Users size={14}/> {home} Formation</h3>
              <div className="formation-display">
                <div className="formation-name" style={{color:'#00d4ff'}}>{r.formations.home?.name}</div>
                <FormationViz formation={r.formations.home?.name} color="#00d4ff"/>
                <p className="formation-desc">{r.formations.home?.description}</p>
              </div>
            </div>
            <div className="card report-section">
              <h3 className="report-section__title"><Users size={14}/> {away} Formation</h3>
              <div className="formation-display">
                <div className="formation-name" style={{color:'#7c3aed'}}>{r.formations.away?.name}</div>
                <FormationViz formation={r.formations.away?.name} color="#7c3aed"/>
                <p className="formation-desc">{r.formations.away?.description}</p>
              </div>
            </div>
          </>}

          {r.tacticalStrengths?.length > 0 && (
            <div className="card report-section report-section--full">
              <h3 className="report-section__title"><TrendingUp size={14}/> Tactical Strengths</h3>
              <div className="strength-grid">
                {r.tacticalStrengths.map((s,i) => (
                  <div key={i} className="strength-card">
                    <div className="strength-card__header">
                      <span className={`badge ${s.team==='home'?'badge-blue':'badge-purple'}`}>{s.team==='home'?home:away}</span>
                      {s.rating && <div className="strength-rating">{s.rating}<span>/10</span></div>}
                    </div>
                    <div className="strength-card__title">{s.title}</div>
                    <p className="strength-card__desc">{s.description}</p>
                    {s.rating && <div className="progress-bar" style={{marginTop:10}}><div className="progress-fill" style={{width:`${s.rating*10}%`}}/></div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {r.tacticalWeaknesses?.length > 0 && (
            <div className="card report-section report-section--full">
              <h3 className="report-section__title"><Shield size={14}/> Tactical Weaknesses</h3>
              <div className="weakness-grid">
                {r.tacticalWeaknesses.map((w,i) => (
                  <div key={i} className="weakness-card">
                    <div className="weakness-card__header">
                      <span style={{fontSize:12,color:'var(--text-muted)',fontWeight:600}}>{w.team==='home'?home:away}</span>
                      <span className={`badge badge-${w.severity==='high'?'red':w.severity==='medium'?'amber':'blue'}`}>{w.severity}</span>
                    </div>
                    <div className="weakness-card__title">{w.title}</div>
                    <p>{w.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {heatRows.length > 0 && (
            <div className="card report-section report-section--full">
              <h3 className="report-section__title">Zone Activity</h3>
              <div className="chart-legend"><span style={{color:'#00d4ff'}}>■ {home}</span><span style={{color:'#7c3aed'}}>■ {away}</span></div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={heatRows} margin={{left:0,right:10}}>
                  <XAxis dataKey="zone" tick={{fill:'#94a3b8',fontSize:12}} axisLine={false} tickLine={false}/>
                  <YAxis domain={[0,100]} tick={{fill:'#475569',fontSize:11}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<ChartTip />}/>
                  <Bar dataKey="home" name={home} fill="#00d4ff" radius={[4,4,0,0]}/>
                  <Bar dataKey="away" name={away} fill="#7c3aed" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ─── PRESSING ─── */}
      {tab === 'pressing' && (
        <div className="tab-grid fade-in">
          {r.pressingAnalysis && [
            {name:home, data:r.pressingAnalysis.home, color:'#00d4ff'},
            {name:away, data:r.pressingAnalysis.away, color:'#7c3aed'},
          ].map(({name,data,color}) => data && (
            <div key={name} className="card report-section">
              <h3 className="report-section__title">{name} — Pressing</h3>
              <div className="pressing-intensity">
                <div className="pressing-score" style={{color}}>{data.intensity}<span>/10</span></div>
                <div className="pressing-label">Press Intensity</div>
                <div className="progress-bar" style={{margin:'10px 0'}}>
                  <div className="progress-fill" style={{width:`${data.intensity*10}%`,background:`linear-gradient(90deg,${color},${color}88)`}}/>
                </div>
              </div>
              <p className="pressing-desc">{data.description}</p>
              {data.triggers?.length > 0 && (
                <div className="pressing-triggers">
                  <div className="pressing-triggers__label">Press Triggers</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:6,marginTop:8}}>
                    {data.triggers.map((t,i) => <span key={i} className="trigger-tag">{t}</span>)}
                  </div>
                </div>
              )}
            </div>
          ))}

          {r.defensiveStructure && [
            {name:home, data:r.defensiveStructure.home},
            {name:away, data:r.defensiveStructure.away},
          ].map(({name,data}) => data && (
            <div key={name} className="card report-section">
              <h3 className="report-section__title">{name} — Defence</h3>
              <div className="def-stats">
                <div className="def-stat"><span>Line</span><strong>{data.line}</strong></div>
                <div className="def-stat"><span>Compactness</span><strong>{data.compactness}/10</strong></div>
              </div>
              <p style={{fontSize:13.5,color:'var(--text-secondary)',lineHeight:1.55,margin:'12px 0'}}>{data.description}</p>
              {data.vulnerabilities?.length > 0 && <>
                <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:8}}>Vulnerabilities</div>
                {data.vulnerabilities.map((v,i) => (
                  <div key={i} className="vuln-item"><AlertCircle size={11} style={{color:'#ef4444',flexShrink:0}}/>{v}</div>
                ))}
              </>}
            </div>
          ))}

          {r.attackingPatterns && [
            {name:home, data:r.attackingPatterns.home},
            {name:away, data:r.attackingPatterns.away},
          ].map(({name,data}) => data && (
            <div key={name} className="card report-section">
              <h3 className="report-section__title"><Swords size={14}/> {name} — Attack</h3>
              <div className="def-stats">
                <div className="def-stat"><span>Style</span><strong>{data.primaryStyle}</strong></div>
                <div className="def-stat"><span>Chances</span><strong>{data.chancesCreated}</strong></div>
              </div>
              <div style={{fontSize:12,color:'var(--text-muted)',margin:'8px 0 4px'}}>Build-Up: <span style={{color:'var(--text-secondary)'}}>{data.buildUpZone}</span></div>
              {data.keyMovements?.length > 0 && <>
                <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',margin:'12px 0 8px'}}>Key Movements</div>
                {data.keyMovements.map((m,i) => <div key={i} className="movement-item">→ {m}</div>)}
              </>}
            </div>
          ))}

          {r.transitionAnalysis && [
            {name:home, data:r.transitionAnalysis.home},
            {name:away, data:r.transitionAnalysis.away},
          ].map(({name,data}) => data && (
            <div key={name} className="card report-section">
              <h3 className="report-section__title">{name} — Transitions</h3>
              <div className="def-stat" style={{marginBottom:12}}>
                <span>Speed</span>
                <strong style={{color:data.speed==='fast'?'#10b981':data.speed==='slow'?'#ef4444':'#f59e0b'}}>{data.speed}</strong>
              </div>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:6}}>OFFENSIVE</div>
                <p style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.5}}>{data.offensiveTransitions}</p>
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:6}}>DEFENSIVE</div>
                <p style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.5}}>{data.defensiveTransitions}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── PLAYERS ─── */}
      {tab === 'players' && (
        <div className="tab-grid fade-in">
          {(r.rosterConfidenceNote || r.playersIdentifiable != null) && (
            <div className="card report-section report-section--full" style={{padding:'14px 18px', display:'flex', alignItems:'flex-start', gap:10}}>
              <AlertCircle size={15} style={{color:'var(--accent-amber)', flexShrink:0, marginTop:2}} />
              <div style={{fontSize:12.5, color:'var(--text-secondary)', lineHeight:1.5}}>
                {r.playersIdentifiable != null && (
                  <div style={{fontWeight:600, color:'var(--text-primary)', marginBottom:2}}>
                    {r.playersIdentifiable} player{r.playersIdentifiable === 1 ? '' : 's'} individually tracked from footage
                  </div>
                )}
                {r.rosterConfidenceNote || 'Stats below are visual estimates from AI review of the footage, not certified match data — treat them as approximate, especially for players marked "low confidence".'}
              </div>
            </div>
          )}
          {r.playerPerformances?.length > 0 ? r.playerPerformances.map((p,i) => (
            <div key={i} className="card player-card">
              <div className="player-card__header">
                <div className="player-avatar">{p.name?.[0] || '?'}</div>
                <div className="player-info">
                  <div className="player-name">{p.name}</div>
                  <div className="player-meta">
                    <span className={`badge ${p.team==='home'?'badge-blue':'badge-purple'}`} style={{fontSize:10}}>
                      {p.team==='home'?home:away}
                    </span>
                    <span style={{color:'var(--text-muted)',fontSize:12,marginLeft:6}}>{p.position}</span>
                  </div>
                </div>
                <div className="player-rating" data-rating={p.rating>=8?'high':p.rating>=6.5?'mid':'low'}>
                  {typeof p.rating==='number' ? p.rating.toFixed(1) : p.rating}
                </div>
              </div>
              <div className="progress-bar" style={{margin:'12px 0 8px'}}>
                <div className="progress-fill" style={{width:`${Math.min(100,(p.rating/10)*100)}%`}}/>
              </div>

              {p.zones && (
                <div style={{ margin: '12px 0', display: 'flex', justifyContent: 'center' }}>
                  <PlayerHeatmap zones={p.zones} color={p.team === 'home' ? '#00d4ff' : '#7c3aed'} />
                </div>
              )}

              {p.stats && (
                <div className="pac-stats" style={{margin:'10px 0'}}>
                  {p.stats.passesAttempted != null && (
                    <div className="pac-stat">
                      <div className="pac-stat__val">{p.stats.passesCompleted ?? '–'}/{p.stats.passesAttempted}</div>
                      <div className="pac-stat__key">Passes</div>
                    </div>
                  )}
                  {p.stats.passAccuracy != null && (
                    <div className="pac-stat">
                      <div className="pac-stat__val">{p.stats.passAccuracy}%</div>
                      <div className="pac-stat__key">Pass Acc.</div>
                    </div>
                  )}
                  {p.stats.shots != null && (
                    <div className="pac-stat">
                      <div className="pac-stat__val">{p.stats.shotsOnTarget ?? 0}/{p.stats.shots}</div>
                      <div className="pac-stat__key">Shots OT</div>
                    </div>
                  )}
                  {p.stats.touches != null && (
                    <div className="pac-stat">
                      <div className="pac-stat__val">{p.stats.touches}</div>
                      <div className="pac-stat__key">Touches</div>
                    </div>
                  )}
                  {p.stats.defensiveActions != null && (
                    <div className="pac-stat">
                      <div className="pac-stat__val">{p.stats.defensiveActions}</div>
                      <div className="pac-stat__key">Def. Actions</div>
                    </div>
                  )}
                </div>
              )}

              {p.statsConfidence && (
                <span
                  className={`badge badge-${p.statsConfidence === 'high' ? 'green' : p.statsConfidence === 'medium' ? 'amber' : 'red'}`}
                  style={{fontSize:10, marginBottom:8, display:'inline-block'}}
                  title="How reliably this player could be tracked across the footage"
                >
                  {p.statsConfidence} confidence
                </span>
              )}

              {p.keyActions?.length > 0 && (
                <div className="player-actions">{p.keyActions.map((a,j) => <span key={j} className="action-tag">{a}</span>)}</div>
              )}
              {p.strengths?.length > 0 && (
                <div className="player-traits">
                  <div className="traits-label">Strengths</div>
                  <div className="traits-list">{p.strengths.map((s,j) => <span key={j} className="trait-tag trait-tag--green">{s}</span>)}</div>
                </div>
              )}
              {p.weaknesses?.length > 0 && (
                <div className="player-traits">
                  <div className="traits-label">Areas to Improve</div>
                  <div className="traits-list">{p.weaknesses.map((w,j) => <span key={j} className="trait-tag trait-tag--red">{w}</span>)}</div>
                </div>
              )}
            </div>
          )) : (
            <div style={{gridColumn:'1/-1',textAlign:'center',padding:40,color:'var(--text-muted)'}}>No player data available</div>
          )}
        </div>
      )}

      {/* ─── OPPOSITION ─── */}
      {tab === 'scouting' && (
        <div className="tab-grid fade-in">
          {r.oppositionTendencies && (
            <div className="card report-section report-section--full">
              <h3 className="report-section__title">Opposition Profile — {away}</h3>
              <p style={{color:'var(--text-secondary)',lineHeight:1.65,fontSize:14}}>{r.oppositionTendencies.description}</p>
            </div>
          )}
          {r.oppositionTendencies?.keyPatterns?.length > 0 && (
            <div className="card report-section">
              <h3 className="report-section__title">Key Patterns</h3>
              {r.oppositionTendencies.keyPatterns.map((p,i) => (
                <div key={i} className="scout-item scout-item--neutral">• {p}</div>
              ))}
            </div>
          )}
          {r.oppositionTendencies?.exploitableAreas?.length > 0 && (
            <div className="card report-section">
              <h3 className="report-section__title">Exploitable Areas</h3>
              {r.oppositionTendencies.exploitableAreas.map((a,i) => (
                <div key={i} className="scout-item scout-item--danger"><Zap size={12} style={{color:'#f59e0b',flexShrink:0}}/>{a}</div>
              ))}
            </div>
          )}
          {r.keyStats && (
            <div className="card report-section report-section--full">
              <h3 className="report-section__title">Full Statistics</h3>
              <div className="full-stats-grid">
                {[
                  {label:'Shots',        hv:r.keyStats.shots?.home,              av:r.keyStats.shots?.away},
                  {label:'On Target',    hv:r.keyStats.shotsOnTarget?.home,      av:r.keyStats.shotsOnTarget?.away},
                  {label:'Corners',      hv:r.keyStats.corners?.home,            av:r.keyStats.corners?.away},
                  {label:'Fouls',        hv:r.keyStats.fouls?.home,              av:r.keyStats.fouls?.away},
                  {label:'Yellow Cards', hv:r.keyStats.yellowCards?.home,        av:r.keyStats.yellowCards?.away},
                  {label:'Pass Accuracy',hv:r.keyStats.passAccuracy?.home!=null?`${r.keyStats.passAccuracy.home}%`:'-', av:r.keyStats.passAccuracy?.away!=null?`${r.keyStats.passAccuracy.away}%`:'-'},
                  {label:'Press %',      hv:r.keyStats.pressureSuccessRate?.home!=null?`${r.keyStats.pressureSuccessRate.home}%`:'-', av:r.keyStats.pressureSuccessRate?.away!=null?`${r.keyStats.pressureSuccessRate.away}%`:'-'},
                ].map((s,i) => (
                  <div key={i} className="full-stat-row">
                    <span className="full-stat-home">{s.hv ?? '-'}</span>
                    <span className="full-stat-label">{s.label}</span>
                    <span className="full-stat-away">{s.av ?? '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── COACHING ─── */}
      {tab === 'coaching' && (
        <div className="tab-grid fade-in">
          <div className="card report-section report-section--full">
            <h3 className="report-section__title"><Zap size={14}/> Coaching Recommendations</h3>
            {r.coachingRecommendations?.length > 0 ? (
              <div className="coaching-list">
                {r.coachingRecommendations.map((rec,i) => (
                  <div key={i} className="coaching-rec" data-priority={rec.priority}>
                    <div className="coaching-rec__header">
                      <span className={`badge badge-${rec.priority==='high'?'red':rec.priority==='medium'?'amber':'green'}`}>{rec.priority} priority</span>
                      <span className="coaching-rec__category">{rec.category}</span>
                    </div>
                    <div className="coaching-rec__text">{rec.recommendation}</div>
                    <div className="coaching-rec__rationale"><strong>Why: </strong>{rec.rationale}</div>
                  </div>
                ))}
              </div>
            ) : <p style={{color:'var(--text-muted)'}}>No coaching recommendations available.</p>}
          </div>

          {r.analystNotes && (
            <div className="card report-section report-section--full">
              <h3 className="report-section__title">Analyst Notes</h3>
              <p style={{color:'var(--text-secondary)',fontSize:13.5,lineHeight:1.65}}>{r.analystNotes}</p>
              <p style={{color:'var(--text-muted)',fontSize:12,marginTop:12}}>
                Generated: {r.generatedAt ? new Date(r.generatedAt).toLocaleString() : 'Unknown'}
                {' · '}{(report.aiGenerated || report.report?.aiGenerated) ? 'Gemini AI' : 'Demo Mode'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FormationViz({ formation, color }) {
  let rows = [1, 4, 3, 3]; // default 4-3-3
  if (formation) {
    const parts = formation.split('-').map(Number).filter(n => !isNaN(n));
    if (parts.length >= 2) rows = [1, ...parts.reverse()];
  }
  return (
    <div className="formation-viz" style={{ '--form-color': color }}>
      {rows.map((count, ri) => (
        <div key={ri} className="formation-row">
          {Array.from({ length: count }).map((_, ci) => (
            <div key={ci} className="formation-dot" />
          ))}
        </div>
      ))}
    </div>
  );
}
